import { useMemo, useState } from 'react';
import { Sidebar } from '../layout/Sidebar';
import { TopBar } from '../layout/TopBar';
import { Bell, ShoppingCart, Gift, Star, AlertTriangle, Users, Check, Trash2, Search, ThumbsUp, ThumbsDown } from 'lucide-react';
import { useOrders } from '../../hooks/useOrders';
import { useNotifications } from '../../hooks/useNotifications';
import { Notification as ApiNotification, notificationsService } from '../../services/notifications.service';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

interface NotificationsPageProps {
  onNavigate: (page: string) => void;
  onOrderClick?: (orderId: string) => void;
}

type NotificationType = 'all' | 'order' | 'inventory' | 'alert' | 'promotion' | 'system';

const STATUS_LABELS: Record<string, string> = {
  PENDING: 'Pending',
  CONFIRMED: 'Confirmed',
  PREPARING: 'Preparing',
  READY: 'Ready',
  DELIVERED: 'Delivered',
  CLOSED: 'Closed',
  CANCELLED: 'Cancelled',
  REJECTED: 'Rejected',
  REFUNDED: 'Refunded',
};

export function NotificationsPage({ onNavigate, onOrderClick }: NotificationsPageProps) {
  const { user } = useAuth();
  const isAdminOrManager = user?.role === 'admin' || user?.role === 'manager';
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth >= 768;
    }
    return true;
  });
  const { orders: ordersData } = useOrders({ page: 1, limit: 50 });
  const [filterType, setFilterType] = useState<NotificationType>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const {
    notifications,
    unreadCount,
    loading,
    error,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    refresh,
  } = useNotifications({ page: 1, limit: 100 });

  const [actionInProgress, setActionInProgress] = useState<string | null>(null);

  const handleApproveStatusChange = async (
    notifId: string,
    _orderId: string,
    orderNumber: string,
    _targetStatus: string
  ) => {
    setActionInProgress(notifId);
    try {
      await notificationsService.approveStatusChangeRequest(notifId);

      // Remove from localStorage pending status changes
      const pending = JSON.parse(localStorage.getItem('qrypt_pending_status_changes') || '{}');
      const notif = notifications.find((n) => n.id === notifId);
      const orderId = notif?.data?.orderId;
      if (orderId) {
        delete pending[orderId];
      }
      localStorage.setItem('qrypt_pending_status_changes', JSON.stringify(pending));

      // Backward compatibility cleanup for old key
      const legacyPending = JSON.parse(localStorage.getItem('qrypt_pending_rejections') || '{}');
      if (orderId) {
        delete legacyPending[orderId];
      }
      localStorage.setItem('qrypt_pending_rejections', JSON.stringify(legacyPending));

      await markAsRead(notifId);
      refresh();
      toast.success(`Order ${orderNumber} status change has been approved and applied`);
    } catch {
      toast.error('Failed to approve status change. Please try again.');
    } finally {
      setActionInProgress(null);
    }
  };

  const handleDenyStatusChange = async (notifId: string, orderNumber: string) => {
    setActionInProgress(notifId);
    try {
      await notificationsService.denyStatusChangeRequest(notifId);

      // Remove from localStorage pending status changes
      const notif = notifications.find((n) => n.id === notifId);
      const orderId = notif?.data?.orderId;
      if (orderId) {
        const pending = JSON.parse(localStorage.getItem('qrypt_pending_status_changes') || '{}');
        delete pending[orderId];
        localStorage.setItem('qrypt_pending_status_changes', JSON.stringify(pending));

        // Backward compatibility cleanup for old key
        const legacyPending = JSON.parse(localStorage.getItem('qrypt_pending_rejections') || '{}');
        delete legacyPending[orderId];
        localStorage.setItem('qrypt_pending_rejections', JSON.stringify(legacyPending));
      }

      await markAsRead(notifId);
      refresh();
      toast.info(`Status change request for Order ${orderNumber} has been denied`);
    } catch {
      toast.error('Failed to deny status change request.');
    } finally {
      setActionInProgress(null);
    }
  };

  const toUiType = (type: ApiNotification['type']): Exclude<NotificationType, 'all'> => {
    switch (type) {
      case 'ORDER':
        return 'order';
      case 'INVENTORY':
        return 'inventory';
      case 'PROMOTION':
        return 'promotion';
      case 'ALERT':
        return 'alert';
      default:
        return 'system';
    }
  };

  const formatRelativeTime = (createdAt: string) => {
    const now = Date.now();
    const then = new Date(createdAt).getTime();
    const diffMinutes = Math.max(1, Math.floor((now - then) / (1000 * 60)));

    if (diffMinutes < 60) return `${diffMinutes} min ago`;
    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
  };

  const notificationsForUi = useMemo(
    () =>
      notifications.map((n) => ({
        id: n.id,
        type: toUiType(n.type),
        title: n.title,
        message: n.message,
        unread: !n.isRead,
        time: formatRelativeTime(n.createdAt),
      })),
    [notifications],
  );

  const getIcon = (type: string) => {
    switch (type) {
      case 'order':
        return <ShoppingCart className="w-5 h-5" />;
      case 'inventory':
        return <Users className="w-5 h-5" />;
      case 'alert':
        return <AlertTriangle className="w-5 h-5" />;
      case 'promotion':
        return <Gift className="w-5 h-5" />;
      case 'system':
        return <Star className="w-5 h-5" />;
      default:
        return <Bell className="w-5 h-5" />;
    }
  };

  const getIconBgColor = (type: string) => {
    switch (type) {
      case 'order':
        return 'bg-blue-100 dark:bg-blue-500/20 text-blue-600 dark:text-blue-400';
      case 'inventory':
        return 'bg-green-100 dark:bg-green-500/20 text-green-600 dark:text-green-400';
      case 'alert':
        return 'bg-orange-100 dark:bg-orange-500/20 text-orange-600 dark:text-orange-400';
      case 'promotion':
        return 'bg-purple-100 dark:bg-purple-500/20 text-purple-600 dark:text-purple-400';
      case 'system':
        return 'bg-indigo-100 dark:bg-indigo-500/20 text-indigo-600 dark:text-indigo-400';
      default:
        return 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400';
    }
  };

  const clearAll = async () => {
    if (window.confirm('Are you sure you want to clear all notifications?')) {
      try {
        await Promise.all(notificationsForUi.map((n) => deleteNotification(n.id)));
      } catch {
        toast.error('Failed to clear notifications');
      }
    }
  };

  const filteredNotifications = notificationsForUi.filter(n => {
    const matchesType = filterType === 'all' || n.type === filterType;
    const matchesSearch = n.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         n.message.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesType && matchesSearch;
  });

  const filterOptions: { value: NotificationType; label: string; icon: any }[] = [
    { value: 'all', label: 'All', icon: Bell },
    { value: 'order', label: 'Orders', icon: ShoppingCart },
    { value: 'inventory', label: 'Inventory', icon: Users },
    { value: 'alert', label: 'Alerts', icon: AlertTriangle },
    { value: 'promotion', label: 'Promotions', icon: Gift },
    { value: 'system', label: 'System', icon: Star },
  ];

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-gray-950">
      <Sidebar 
        activePage="notifications" 
        onNavigate={onNavigate} 
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        ordersCount={ordersData.length}
      />
      
      <div className={`flex-1 flex flex-col overflow-hidden transition-all duration-300 ${
        sidebarOpen ? 'min-[768px]:ml-64' : 'ml-0'
      }`}>
        <TopBar 
          onMenuClick={() => setSidebarOpen(!sidebarOpen)} 
          sidebarOpen={sidebarOpen}
          onNavigate={onNavigate}
        />
        
        <main className="flex-1 overflow-y-auto p-6">
          <div className="max-w-[1400px] mx-auto">
            {/* Header */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Notifications</h1>
                <div className="flex items-center gap-3">
                  {unreadCount > 0 && (
                    <button
                      onClick={() => markAllAsRead().catch(() => toast.error('Failed to mark notifications as read'))}
                      className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 transition-colors"
                    >
                      <Check className="w-4 h-4" />
                      Mark all as read
                    </button>
                  )}
                  {notifications.length > 0 && (
                    <button
                      onClick={clearAll}
                      className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                      Clear all
                    </button>
                  )}
                </div>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {unreadCount > 0 
                  ? `You have ${unreadCount} unread notification${unreadCount > 1 ? 's' : ''}`
                  : 'You\'re all caught up!'
                }
              </p>
            </div>

            {/* Filters and Search */}
            <div className="mb-6 bg-white dark:bg-gray-900 rounded-xl p-4 border border-gray-200 dark:border-gray-800">
              <div className="flex flex-col lg:flex-row gap-4">
                {/* Filter Buttons */}
                <div className="flex flex-wrap gap-2">
                  {filterOptions.map((option) => {
                    const Icon = option.icon;
                    const count = option.value === 'all' 
                      ? notificationsForUi.length 
                      : notificationsForUi.filter(n => n.type === option.value).length;
                    
                    return (
                      <button
                        key={option.value}
                        onClick={() => setFilterType(option.value)}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                          filterType === option.value
                            ? 'bg-indigo-600 text-white shadow-md'
                            : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                        {option.label}
                        {count > 0 && (
                          <span className={`px-2 py-0.5 rounded-full text-xs ${
                            filterType === option.value
                              ? 'bg-white/20 text-white'
                              : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                          }`}>
                            {count}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* Search */}
                <div className="relative flex-1 lg:max-w-xs">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search notifications..."
                    className="w-full pl-10 pr-4 py-2 text-sm bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>
            </div>

            {error && (
              <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-900/20 dark:text-red-300">
                {error}
              </div>
            )}

            {/* Notifications List */}
            {loading ? (
              <div className="bg-white dark:bg-gray-900 rounded-xl p-12 text-center border border-gray-200 dark:border-gray-800">
                <p className="text-sm text-gray-600 dark:text-gray-400">Loading notifications...</p>
              </div>
            ) : filteredNotifications.length === 0 ? (
              <div className="bg-white dark:bg-gray-900 rounded-xl p-12 text-center border border-gray-200 dark:border-gray-800">
                <Bell className="w-16 h-16 text-gray-300 dark:text-gray-700 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  No notifications found
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {searchQuery || filterType !== 'all'
                    ? 'Try adjusting your filters or search query'
                    : 'All notifications have been cleared'
                  }
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredNotifications.map((notification) => {
                  const rawNotif = notifications.find((n) => n.id === notification.id);
                  const requestType = rawNotif?.data?.type as string | undefined;
                  const approvalResolved = !!(rawNotif?.data?.approvalStatus);
                  const isStatusChangeRequest =
                    !approvalResolved &&
                    (requestType === 'ORDER_STATUS_CHANGE_REQUEST' ||
                    requestType === 'REJECTION_REQUEST' ||
                    notification.title.toLowerCase().includes('approval required'));
                  const requestOrderId = rawNotif?.data?.orderId as string | undefined;
                  const notificationOrderId = rawNotif?.data?.orderId as string | undefined;
                  const requestOrderNumber = (rawNotif?.data?.orderNumber || requestOrderId || '') as string;
                  const requestTargetStatus = (rawNotif?.data?.requestedStatus || (requestType === 'REJECTION_REQUEST' ? 'REJECTED' : undefined)) as string | undefined;
                  const requestTargetStatusLabel = STATUS_LABELS[requestTargetStatus || ''] || requestTargetStatus || 'Requested Status';
                  const requestRequester = (rawNotif?.data?.requestedByName || 'Someone') as string;
                  const isActioning = actionInProgress === notification.id;
                  const canOpenOrder = !!notificationOrderId && !!onOrderClick;

                  const handleNotificationClick = () => {
                    if (!notificationOrderId || !onOrderClick) return;

                    // Persist pending status-change context for OrderDetails page so it stays actionable after navigation.
                    if (isStatusChangeRequest) {
                      const pending = JSON.parse(localStorage.getItem('qrypt_pending_status_changes') || '{}');
                      pending[notificationOrderId] = {
                        orderNumber: requestOrderNumber || notificationOrderId,
                        requestedStatus: requestTargetStatus || 'REJECTED',
                        requestedByName: requestRequester,
                        notificationId: notification.id,
                        requestedAt: rawNotif?.createdAt || new Date().toISOString(),
                      };
                      localStorage.setItem('qrypt_pending_status_changes', JSON.stringify(pending));
                    }

                    if (notification.unread && !isStatusChangeRequest) {
                      markAsRead(notification.id).catch(() => {
                        toast.error('Failed to mark notification as read');
                      });
                    }

                    onOrderClick(notificationOrderId);
                  };

                  return (
                  <div
                    key={notification.id}
                    onClick={handleNotificationClick}
                    className={`rounded-xl p-4 border transition-all hover:shadow-md ${
                      isStatusChangeRequest && isAdminOrManager
                        ? 'border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10'
                        : notification.unread
                        ? 'border-indigo-200 dark:border-indigo-500/30 bg-indigo-50/80 dark:bg-indigo-500/10'
                        : 'border-gray-200 dark:border-gray-700/60 bg-white dark:bg-gray-900'
                    } ${canOpenOrder ? 'cursor-pointer' : ''}`}
                  >
                    <div className="flex items-start gap-4">
                      {/* Icon */}
                        <div className={`p-3 rounded-lg ${isStatusChangeRequest && isAdminOrManager ? 'bg-amber-100 dark:bg-amber-500/20 text-amber-600 dark:text-amber-400' : getIconBgColor(notification.type)}`}>
                        {getIcon(notification.type)}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-4 mb-1">
                          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                            {notification.title}
                            {notification.unread && (
                              <span className="ml-2 inline-block w-2 h-2 bg-indigo-600 rounded-full"></span>
                            )}
                            {isStatusChangeRequest && isAdminOrManager && (
                              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-200 dark:bg-amber-500/30 text-amber-800 dark:text-amber-300">
                                Action Required
                              </span>
                            )}
                          </h3>
                          <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                            {notification.time}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                          {notification.message}
                        </p>

                        {/* Status-change approval actions */}
                        {isStatusChangeRequest && isAdminOrManager && (
                          <div className="flex items-center gap-2 mt-3 mb-1">
                            <button
                              disabled={isActioning}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleApproveStatusChange(notification.id, requestOrderId || '', requestOrderNumber, requestTargetStatus || 'REJECTED');
                              }}
                              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                            >
                              <ThumbsUp className="w-3.5 h-3.5" />
                              Approve {requestTargetStatusLabel || 'Request'}
                            </button>
                            <button
                              disabled={isActioning}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDenyStatusChange(notification.id, requestOrderNumber);
                              }}
                              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
                            >
                              <ThumbsDown className="w-3.5 h-3.5" />
                              Deny
                            </button>
                            <span className="text-xs text-gray-500 dark:text-gray-400 ml-1">
                              Requested by {requestRequester}
                            </span>
                          </div>
                        )}

                        <div className="flex items-center gap-2">
                          {notification.unread && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                markAsRead(notification.id).catch(() => toast.error('Failed to mark notification as read'));
                              }}
                              className="text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 transition-colors"
                            >
                              Mark as read
                            </button>
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteNotification(notification.id).catch(() => toast.error('Failed to delete notification'));
                            }}
                            className="text-xs font-medium text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 transition-colors"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}